import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Checkbox,
  FormControlLabel,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  AuthorizedPerson,
  addAuthorizedPerson,
  updateAuthorizedPerson,
  deleteAuthorizedPerson,
} from '../api/families';
import { RELATIONSHIP_OPTIONS } from '../constants/relationships';
import AuthorizedPersonCard from './AuthorizedPersonCard';

interface AuthorizedPersonsListProps {
  authorizedPersons: AuthorizedPerson[];
  onPersonUpdated: () => void;
}

const AuthorizedPersonsList: React.FC<AuthorizedPersonsListProps> = ({
  authorizedPersons,
  onPersonUpdated,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingPerson, setEditingPerson] = useState<AuthorizedPerson | null>(null);
  const [deletingPerson, setDeletingPerson] = useState<AuthorizedPerson | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    relationship: '',
    phone: '',
    email: '',
    has_pickup_rights: true,
    id_check_required: false,
  });

  const handleOpenAdd = () => {
    setEditingPerson(null);
    setFormData({
      first_name: '',
      last_name: '',
      relationship: '',
      phone: '',
      email: '',
      has_pickup_rights: true,
      id_check_required: false,
    });
    setError(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = (person: AuthorizedPerson) => {
    setEditingPerson(person);
    setFormData({
      first_name: person.first_name,
      last_name: person.last_name,
      relationship: person.relationship,
      phone: person.phone,
      email: person.email || '',
      has_pickup_rights: person.has_pickup_rights,
      id_check_required: person.id_check_required,
    });
    setError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPerson(null);
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name || !formData.relationship || !formData.phone) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingPerson) {
        await updateAuthorizedPerson(editingPerson.id, formData);
      } else {
        await addAuthorizedPerson(formData);
      }
      onPersonUpdated();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save authorized person');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDelete = (person: AuthorizedPerson) => {
    setDeletingPerson(person);
    setError(null);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeletingPerson(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingPerson) return;

    setLoading(true);
    setError(null);

    try {
      await deleteAuthorizedPerson(deletingPerson.id);
      onPersonUpdated();
      handleCloseDeleteDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete authorized person');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 2
      }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          Authorized Persons
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={handleOpenAdd}
          fullWidth={isMobile}
          sx={{
            minHeight: { xs: 44, sm: 36 },
            fontSize: { xs: '0.9375rem', sm: '0.875rem' }
          }}
        >
          Add Person
        </Button>
      </Box>

      {authorizedPersons.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            No authorized persons added yet. Click "Add Person" to get started.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: { xs: 2, sm: 2, md: 3 },
          }}
        >
          {authorizedPersons.map((person) => (
            <AuthorizedPersonCard
              key={person.id}
              person={person}
              onEdit={handleOpenEdit}
              onDelete={handleOpenDelete}
            />
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {editingPerson ? 'Edit Authorized Person' : 'Add Authorized Person'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              required
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            />
            <TextField
              label="Last Name"
              required
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <TextField
              label="Relationship"
              required
              select
              value={formData.relationship}
              onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
            >
              {RELATIONSHIP_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Phone"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="555-1234"
            />
          </Box>
          <TextField
            label="Email (Optional)"
            fullWidth
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.has_pickup_rights}
                onChange={(e) => setFormData({ ...formData, has_pickup_rights: e.target.checked })}
              />
            }
            label="Authorized to pick up students"
            sx={{ mt: 2 }}
          />
          {formData.has_pickup_rights && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.id_check_required}
                  onChange={(e) => setFormData({ ...formData, id_check_required: e.target.checked })}
                />
              }
              label="Require ID verification at pickup"
              sx={{ mt: 1, ml: 4 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 }, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={loading}
            fullWidth={isMobile}
            sx={{ minHeight: { xs: 44, sm: 36 } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            fullWidth={isMobile}
            sx={{ minHeight: { xs: 44, sm: 36 } }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to remove{' '}
            <strong>
              {deletingPerson?.first_name} {deletingPerson?.last_name}
            </strong>{' '}
            from your authorized persons list? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 }, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={loading}
            fullWidth={isMobile}
            sx={{ minHeight: { xs: 44, sm: 36 } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={loading}
            fullWidth={isMobile}
            sx={{ minHeight: { xs: 44, sm: 36 } }}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuthorizedPersonsList;
